import {BASEURL, BASENAME, VERSION, APIKEY} from "./api";

export const endpoints = [
  {
    title: 'Spacecraft DB',
    method: 'GET',
    path: '/spacecraft-db',
    description: 'Retrieve available spacecraft IDs and their respective (data availability) start times and end times.',
    examples: [
      {
        description: 'Fetch available spacecraft data.',
        code: [
          {
            form: 'cURL',
            code:
`curl -L '${BASEURL}/api/spacecraft-db' \\
-H 'Accept: application/json' \\
-H 'Authorization: Api-Key ${APIKEY}'
`
          },
          {
            form: 'Python',
            code:
`import requests

url = '${BASEURL}/api/spacecraft-db'

headers = {
    'Accept': 'application/json',
    'Authorization': 'Api-Key ${APIKEY}'
}

response = requests.get(url, headers=headers)
data = response.json()`,
          },
          {
            form: 'HTTP',
            code:
`GET /v${VERSION}/api/spacecraft-db HTTP/1.1
Host: ${BASENAME}
Accept: application/json
Authorization: Api-Key ${APIKEY}`,
          },
        ]
      },
    ],
  },
  {
    title: 'MAS Run DB',
    method: 'GET',
    path: '/mas-run-db',
    description: 'Retrieve a list of available runs from the MAS run database.',
    notes: 'Each element in this list is a mapping containing a run\'s: ID, name, type, descriptor, model summary, surface data source, coronal model, time range, domain availability, and mesh resolution. The MAS run database query can be filtered with the following query parameters (which, when provided, are applied conjunctively).',
    queryParams: [
      {
        name: 'toi',
        type: 'datetime',
        options: 'ISO 8601',
        description: 'Time of interest (optional): return runs that include this time in their time range.',
      },
      {
        name: 't0',
        type: 'datetime',
        options: 'ISO 8601',
        description: 'Time range start (optional): return runs that start on or after this time.',
      },
      {
        name: 't1',
        type: 'datetime',
        options: 'ISO 8601',
        description: 'Time range end (optional): return runs that end on or before this time.',
      },
      {
        name: 'model',
        type: 'array[string]',
        options: 'thermo_1, thermo_2, poly, cme, psp, eclipse, carrington',
        description: 'Model type (optional): return runs that are of a particular model type (applied disjunctively).',
      },
      {
        name: 'type',
        type: 'array[string]',
        options: 'ss, td, co',
        description: 'Run type (optional): return runs that are e.g. Steady-State, Time-Dependent, or Collections (applied disjunctively).',
      },
      {
        name: 'domain',
        type: 'array[string]',
        options: 'cor, hel',
        description: 'Available domains (optional): return runs that have a Coronal or Heliospheric domain (applied disjunctively).',
      },
      {
        name: 'variables',
        type: 'array[string]',
        options: 'br, bt, bp, jr, jt, jp, vr, vt, vp, va, rho, t, p, ep, em, zp, zm, heat',
        description: 'Available variables (optional): return runs that have a particular MHD variable file in their output (applied disjunctively).',
      },
    ],
    examples: [
      {
        description: 'Fetch all MAS runs.',
        code: [
          {
            form: 'cURL',
            code:
`curl -G -L '${BASEURL}/api/mas-run-db' \\
-H 'Accept: application/json' \\
-H 'Authorization: Api-Key ${APIKEY}'
`
          },
          {
            form: 'Python',
            code:
`import requests

url = '${BASEURL}/api/mas-run-db'

headers = {
    'Accept': 'application/json',
    'Authorization': 'Api-Key ${APIKEY}'
}

response = requests.get(url, headers=headers)
data = response.json()`,
          },
          {
            form: 'HTTP',
            code:
`GET /v${VERSION}/api/mas-run-db HTTP/1.1
Host: ${BASENAME}
Accept: application/json
Authorization: Api-Key ${APIKEY}`,
          },
        ]
      },
      {
        description: 'Fetch all MAS runs that fall within the 23<sup>rd</sup> solar cycle.',
        code: [
          {
            form: 'cURL',
            code:
`curl -G -L '${BASEURL}/api/mas-run-db' \\
-H 'Accept: application/json' \\
-H 'Authorization: Api-Key ${APIKEY}' \\
--data-urlencode 't0=1996-08-01T00:00:00' \\
--data-urlencode 't1=2008-12-01T00:00:00' \\
`
          },
          {
            form: 'Python',
            code:
`import requests

url = '${BASEURL}/api/mas-run-db'

headers = {
    'Accept': 'application/json',
    'Authorization': 'Api-Key ${APIKEY}'
}

params = {
    't0': '1996-08-01T00:00:00',
    't1': '2008-12-01T00:00:00',
}

response = requests.get(url, headers=headers, params=params)
data = response.json()`,
          },
          {
            form: 'HTTP',
            code:
`GET /v${VERSION}/api/mas-run-db?t0=1996-08-01T00%3A00%3A00&t1=2008-12-01T00%3A00%3A00 HTTP/1.1
Host: ${BASENAME}
Accept: application/json
Authorization: Api-Key ${APIKEY}`,
          },
        ]
      },
      {
        description: 'Fetch all steady state and time dependent MAS runs that contain the 2024 Total Solar Eclipse (April 8, 2024) in their time range, and have the coronal magnetic field vector available.',
        code: [
          {
            form: 'cURL',
            code:
`curl -G -L '${BASEURL}/api/mas-run-db' \\
-H 'Accept: application/json' \\
-H 'Authorization: Api-Key ${APIKEY}' \\
--data-urlencode 'toi=2024-04-08T09:00:00' \\
--data-urlencode 'model=thermo_2' \\
--data-urlencode 'type=ss' \\
--data-urlencode 'type=td' \\
--data-urlencode 'domain=cor' \\
--data-urlencode 'variables=br' \\
--data-urlencode 'variables=bt' \\
--data-urlencode 'variables=bp'
`
          },
          {
            form: 'Python',
            code:
`import requests

url = '${BASEURL}/api/mas-run-db'

headers = {
    'Accept': 'application/json',
    'Authorization': 'Api-Key ${APIKEY}'
}

params = {
    'toi': '2024-04-08T09:00:00',
    'model': 'thermo_2',
    'type': ['ss', 'td'],
    'domain': 'cor',
    'variables': ['br', 'bt', 'bp']
}

response = requests.get(url, headers=headers, params=params)
data = response.json()`,
          },
          {
            form: 'HTTP',
            code:
`GET /v${VERSION}/api/mas-run-db?toi=2024-04-08T09%3A00%3A00&model=thermo_2&type=ss&type=td&domain=cor&variables=br&variables=bt&variables=bp HTTP/1.1
Host: ${BASENAME}
Accept: application/json
Authorization: Api-Key ${APIKEY}`,
          },
        ]
      },
    ],
  },
  {
    title: 'Run Metadata',
    method: 'GET',
    path: '/mas-run-db/{cor_id}',
    description: 'Retrieve metadata for a specific MAS run by ID.',
    notes: 'The returned mapping contains the run\'s output (omas) file, and – for each domain, coronal ("<b><code>cor</code></b>") and heliospheric ("<b><code>hel</code></b>") – lists of available states and variable quantities. "Time dependent" and "collection" runs contain an ordered list of states (<i>n</i> > 1), while "steady state" runs contain a list with exactly one state (<i>n</i> = 1). If a run does not contain a particular domain, the corresponding list of states and variables will be empty.',
    pathParams: [
      {
        name: 'cor_id',
        type: 'integer',
        options: null,
        description: 'MAS run ID.',
      },
    ],
    examples: [
      {
        description: 'Fetch metadata for MAS run with ID 37',
        code: [
          {
            form: 'cURL',
            code:
`curl -L '${BASEURL}/api/mas-run-db/37' \\
-H 'Accept: application/json' \\
-H 'Authorization: Api-Key ${APIKEY}'
`
          },
          {
            form: 'Python',
            code:
`import requests

cor_id = 37
url = f'${BASEURL}/api/mas-run-db/{cor_id}'

headers = {
    'Accept': 'application/json',
    'Authorization': 'Api-Key ${APIKEY}'
}

response = requests.get(url, headers=headers)
data = response.json()`,
          },
          {
            form: 'HTTP',
            code:
`GET /v${VERSION}/api/mas-run-db/37 HTTP/1.1
Host: ${BASENAME}
Accept: application/json
Authorization: Api-Key ${APIKEY}`,
          },
        ]
      },
    ],
  },
  {
    title: 'Fetch Files',
    method: 'GET',
    path: '/mas-run-db/{cor_id}/{domain}/{state}/{variable}',
    description: 'Fetch MHD variable files for a given MAS run, domain, state, and variable. The requested HDF5 files are returned in a zip archive.',
    notes: 'State Selection:' +
      '<blockquote>The state of interest can be specified as a state index (<i>e.g.</i> 0, 1, 2, ...) or an ISO 8601 datetime string (<i>e.g.</i> "2025-01-01T00:00:00"). If an index is supplied, the corresponding state – which can be found using the <b><code>GET /mas-run-db/{cor_id}</code></b> endpoint – will be selected; if a datetime string is supplied, the closest state to that time will be returned.</blockquote>' +
      'Variable Selection:' +
      '<blockquote>If the <b><code>variable</code></b> parameter is set to "all", all available variables files for the given run and domain will be returned; otherwise, the requested variables can be specified as a string of comma-separated variable names (<i>e.g.</i> "br,bt,bp,rho"). Note, not all runs contain all variables; if a requested variable is not available for the given run and domain, this file will not be returned.</blockquote>' +
      'Large Requests:' +
      '<blockquote> This API retrieves data files for a single state within a MAS simulation run. For high-volume data requests — such as time‐dependent simulations or multiple steady-state cases — it is recommended to coordinate directly with the Predictive Science Inc. team to ensure efficient and reliable data transfer.\n' +
      '</blockquote>\n',
    pathParams: [
      {
        name: 'cor_id',
        type: 'integer',
        options: null,
        description: 'MAS run ID.',
      },
      {
        name: 'domain',
        type: 'string',
        options: 'cor, hel',
        description: 'Simulation domain.',
      },
      {
        name: 'state',
        type: 'integer | datetime',
        options: '[-1, 0, 1, ...] | ISO 8601',
        description: 'State index | Time of interest.',
      },
      {
        name: 'variable',
        type: 'string',
        options: 'all, br, bt, bp, jr, jt, jp, vr, vt, vp, va, rho, t, p, ep, em, zp, zm, heat',
        description: 'Requested MHD quantities (HDF5 files).',
      },
    ],
    examples: [
      {
        description: 'Fetch all available coronal MHD variable files for MAS run 37; the first (and only) state of the steady state run will be selected using state index 0.',
        code: [
          {
            form: 'cURL',
            code:
`curl -L '${BASEURL}/api/mas-run-db/37/cor/0/all' \\
-H 'Authorization: Api-Key ${APIKEY}' \\
-o mas_run_37_cor_first_all.zip 
`
          },
          {
            form: 'Python',
            code:
`import requests

url = '${BASEURL}/api/mas-run-db/'

headers = {
    'Authorization': 'Api-Key ${APIKEY}'
}

path_params = {
    'cor_id': '37',
    'domain': 'cor',
    'state': '0',
    'variable': 'all'
}

response = requests.get(url + '/'.join(path_params.values()), headers=headers, stream=True)
response.raise_for_status()
with open('mas_run_37_cor_first_all.zip', 'wb') as f:
    for chunk in response.iter_content(chunk_size=8192):
        f.write(chunk)
`,
          },
          {
            form: 'HTTP',
            code:
`GET /v${VERSION}/api/mas-run-db/37/cor/0/all HTTP/1.1
Host: ${BASENAME}
Authorization: Api-Key ${APIKEY}`,
          },
        ]
      },
      {
        description: 'Fetch the coronal velocity files for MAS run 2013; the final state of this time dependent run will be selected using state index -1.',
        code: [
          {
            form: 'cURL',
            code:
`curl -L '${BASEURL}/api/mas-run-db/2013/cor/-1/vr,vt,vp' \\
-H 'Authorization: Api-Key ${APIKEY}' \\
-o mas_run_2013_cor_last_velocity.zip 
`
          },
          {
            form: 'Python',
            code:
`import requests

url = '${BASEURL}/api/mas-run-db/'

headers = {
    'Authorization': 'Api-Key ${APIKEY}'
}

path_params = {
    'cor_id': '2013',
    'domain': 'cor',
    'state': '-1',
    'variable': 'vr,vt,vp'
}

response = requests.get(url + '/'.join(path_params.values()), headers=headers, stream=True)
response.raise_for_status()
with open('mas_run_2013_cor_last_velocity.zip', 'wb') as f:
    for chunk in response.iter_content(chunk_size=8192):
        f.write(chunk)
`,
          },
          {
            form: 'HTTP',
            code:
`GET /v${VERSION}/api/mas-run-db/2013/cor/-1/vr,vt,vp HTTP/1.1
Host: ${BASENAME}
Authorization: Api-Key ${APIKEY}`,
          },
        ]
      },
      {
        description: 'Fetch the density file for MAS collection run 2016; the state closest to the time of interest (2024-04-08T09:00:00) will be selected.',
        code: [
          {
            form: 'cURL',
            code:
`curl -L '${BASEURL}/api/mas-run-db/2016/cor/2024-04-08T09:00:00/rho' \\
-H 'Authorization: Api-Key ${APIKEY}' \\
-o mas_run_2016_cor_eclipse_rho.zip 
`
          },
          {
            form: 'Python',
            code:
`import requests

url = '${BASEURL}/api/mas-run-db/'

headers = {
    'Authorization': 'Api-Key ${APIKEY}'
}

path_params = {
    'cor_id': '2016',
    'domain': 'cor',
    'state': '2024-04-08T09:00:00',
    'variable': 'rho'
}

response = requests.get(url + '/'.join(path_params.values()), headers=headers, stream=True)
response.raise_for_status()
with open('mas_run_2016_cor_eclipse_rho.zip ', 'wb') as f:
    for chunk in response.iter_content(chunk_size=8192):
        f.write(chunk)
`,
          },
          {
            form: 'HTTP',
            code:
`GET /v${VERSION}/api/mas-run-db/2016/cor/2024-04-08T09:00:00/rho HTTP/1.1
Host: ${BASENAME}
Authorization: Api-Key ${APIKEY}`,
          },
        ]
      },
    ],
  },
  {
    title: 'Slice Data',
    method: 'GET',
    path: '/slice-3d/{cor_id}/{domain}/{state}/{variable}',
    description: 'Linearly interpolate a 3D datafile – for a given MAS run, domain, state, and variable – along a specified axis (or axes).',
    notes: 'Slicing is performed on a single MHD quanity datafile, specified by the given path parameters. Each datafile exists on a 3D mesh of spherical coordinates, <i>i.e.</i> r (radius), t (theta), p (phi).  For each <b><code>"r", "t", "p"</code></b> query parameter supplied, the cooresponding dimension is fixed at the provided value, and linearly interpolated along the remaining dimensions. If a single dimension is queried, a 2D slice is returned; if two dimensions are queried, a 1D slice is returned; if all three dimensions are queried, a single point is returned. Responses for <b><code>/slice-3d</code></b> requests are JSON structures with keys: <b><code>"values"</code></b> (the sliced <i>n</i> dimensional data, where n ∈ {0, 1, 2}<i></i>), <b><code>"r_scale"</code></b>, <b><code>"t_scale"</code></b>, <b><code>"p_scale"</code></b>, and <b><code>"meta"</code></b> (a dictionary of metadata describing the request parameters, scale/value units, and MAS run).' +
      '<br>' +
      '<h3>Considerations</h3>' +
      'Datafile Scales:' +
      '<blockquote>Radial scales (units: R⊙) have a range contingent on the solution\'s domain (generally between 1R⊙ and 30R⊙ for "Coronal" domains, and between 30R⊙ and 236.46R⊙ for "Heliospheric" domains). Theta scales (units: radians) have a co-latitudinal range between 0 and 𝜋. Phi scales (units: radians) have a longitudinal range between 0 and 2𝜋.</blockquote>' +
      'Range Handling:' +
      '<blockquote>If a slice value is provieded that falls outside the corresponding dimension\'s range (<i>see above</i>), a <b>400</b> response will be issued containing the appropriate lower and upper limits for the given scale.</blockquote>' +
      'State Selection:' +
      '<blockquote>The state of interest can be specified as a state index (<i>e.g.</i> 0, 1, 2, ...) or an ISO 8601 datetime string (<i>e.g.</i> "2025-01-01T00:00:00"). If an index is supplied, the corresponding state – which can be found using the <b><code>GET /mas-run-db/{cor_id}</code></b> endpoint – will be selected; if a datetime string is supplied, the closest state to that time will be returned.</blockquote>',
      // 'Slice Selection:' +
      // '<blockquote></blockquote>',
    pathParams: [
      {
        name: 'cor_id',
        type: 'integer',
        options: null,
        description: 'MAS run ID.',
      },
      {
        name: 'domain',
        type: 'string',
        options: 'cor, hel',
        description: 'Simulation domain.',
      },
      {
        name: 'state',
        type: 'integer | datetime',
        options: '[-1, 0, 1, ...] | ISO 8601',
        description: 'State index | Time of interest.',
      },
      {
        name: 'variable',
        type: 'string',
        options: 'br, bt, bp, jr, jt, jp, vr, vt, vp, va, rho, t, p, ep, em, zp, zm, heat',
        description: 'Requested MHD quantity.',
      },
    ],
    queryParams: [
      {
        name: 'r',
        type: 'double',
        options: null,
        description: 'Radial scale value to slice at (units: R⊙).',
      },
      {
        name: 't',
        type: 'double',
        options: null,
        description: 'Theta scale value to slice at (units: radians).',
      },
      {
        name: 'p',
        type: 'double',
        options: null,
        description: 'Phi scale value to slice at (units: radians).',
      },
    ],
    examples: [
      {
        description: 'Select the radial component of the magnetic field for the coronal solution of MAS run 37 (using the first/only state of the steady state run); slice the 3D datafile at the photosphere (r = 1.0 R⊙) and the equatorial plane (𝜃 = 𝜋/2), returning a 1D slice.',
        code: [
          {
            form: 'cURL',
            code:
`curl -G -L '${BASEURL}/api/slice-3d/37/cor/0/br' \\
-H 'Accept: application/json' \\
-H 'Authorization: Api-Key ${APIKEY}' \\
--data-urlencode 'r=1' \\
--data-urlencode 't=1.57079632679'
`
          },
          {
            form: 'Python',
            code:
`import requests
from math import pi

url = '${BASEURL}/api/slice-3d/'

headers = {
    'Authorization': 'Api-Key ${APIKEY}'
}

path_params = {
    'cor_id': '37',
    'domain': 'cor',
    'state': '0',
    'variable': 'br'
}

query_params = {
    'r': 1.0,
    't': pi/2
}

response = requests.get(url + '/'.join(path_params.values()), headers=headers, params=query_params)
response.raise_for_status()
data = response.json()

######################################################
# To process the returned data using numpy and astropy
######################################################

from numpy import array
from astropy.units import Unit
for key in ('r_scale', 't_scale', 'p_scale'):
    data[key] = array(data[key]) * Unit(data['meta']['data'][f'{key}_units'])
scale, unit = data['meta']['data']['variable_conversion'].split(None, 1)
data['values'] = array(data['values']) * float(scale) * Unit(unit)
`,
          },
          {
            form: 'HTTP',
            code:
`GET /v${VERSION}/api/slice-3d/37/cor/0/br?r=1.0&t=1.5707963267948966 HTTP/1.1 
Host: ${BASENAME}
Authorization: Api-Key ${APIKEY}`,
          },
        ]
      },
      {
        description: 'Select the radial component of velocity for the coronal solution of MAS run 2013 (using the final state of the time dependent run with state index -1); slice the 3D datafile at the longitudinal midpoint (𝜙 = 𝜋), returning a 2D slice.',
        code: [
          {
            form: 'cURL',
            code:
`curl -G -L '${BASEURL}/api/slice-3d/2013/cor/-1/vr' \\
-H 'Accept: application/json' \\
-H 'Authorization: Api-Key ${APIKEY}' \\
--data-urlencode 'p=3.14159265359 ' \\
`
          },
          {
            form: 'Python',
            code:
`import requests
from math import pi

url = '${BASEURL}/api/slice-3d/'

headers = {
    'Authorization': 'Api-Key ${APIKEY}'
}

path_params = {
    'cor_id': '2013',
    'domain': 'cor',
    'state': '-1',
    'variable': 'vr'
}

query_params = {
    'p': pi,
}

response = requests.get(url + '/'.join(path_params.values()), headers=headers, params=query_params)
response.raise_for_status()
data = response.json()

######################################################
# To process the returned data using numpy and astropy
######################################################

from numpy import array
from astropy.units import Unit
for key in ('r_scale', 't_scale', 'p_scale'):
    data[key] = array(data[key]) * Unit(data['meta']['data'][f'{key}_units'])
scale, unit = data['meta']['data']['variable_conversion'].split(None, 1)
data['values'] = array(data['values']) * float(scale) * Unit(unit)
`,
          },
          {
            form: 'HTTP',
            code:
`GET /v${VERSION}/api/slice-3d/2013/cor/-1/vr?p=3.141592653589793 HTTP/1.1
Host: ${BASENAME}
Authorization: Api-Key ${APIKEY}`,
          },
        ]
      },
      {
        description: 'Fetch the density file for the heliospheric solution of MAS collection run 2016, where the state closest to the time of interest (2024-04-08T09:00:00) will be selected; slice the 3D datafile at r = 30.0R⊙, 𝜃 = 𝜋/3, and 𝜙 = 3𝜋/2, returning a single point.',
        code: [
          {
            form: 'cURL',
            code:
`curl -G -L '${BASEURL}/api/slice-3d/2016/hel/2024-04-08T09:00:00/rho' \\
-H 'Accept: application/json' \\
-H 'Authorization: Api-Key ${APIKEY}' \\
--data-urlencode 'r=30.0 ' \\
--data-urlencode 't=1.04719755 ' \\
--data-urlencode 'p=4.71238898 '
`
          },
          {
            form: 'Python',
            code:
`import requests
from math import pi

url = '${BASEURL}/api/slice-3d/'

headers = {
    'Authorization': 'Api-Key ${APIKEY}'
}

path_params = {
    'cor_id': '2016',
    'domain': 'hel',
    'state': '2024-04-08T09:00:00',
    'variable': 'rho'
}

query_params = {
    'r': 30.0,
    't': pi / 3,
    'p': 3 * pi / 2,
}

response = requests.get(url + '/'.join(path_params.values()), headers=headers, params=query_params)
response.raise_for_status()
data = response.json()

######################################################
# To process the returned data using numpy and astropy
######################################################

from numpy import array
from astropy.units import Unit
for key in ('r_scale', 't_scale', 'p_scale'):
    data[key] = array(data[key]) * Unit(data['meta']['data'][f'{key}_units'])
scale, unit = data['meta']['data']['variable_conversion'].split(None, 1)
data['values'] = array(data['values']) * float(scale) * Unit(unit)
`,
          },
          {
            form: 'HTTP',
            code:
`GET /v${VERSION}/api/slice-3d/2016/hel/2024-04-08T09:00:00/rho?r=30.0&t=1.04719755&p=4.71238898 HTTP/1.1
Host: ${BASENAME}
Authorization: Api-Key ${APIKEY}`,
          },
        ]
      },
    ],
  },
  {
    title: 'Time Series',
    method: 'GET',
    path: '/time-series/{cor_id}/{sc_id}/{domain}/{variable}',
    description: 'Retrieve the time series comparison for a given MAS run, spacecraft, domain, and quantity.',
    notes: 'Time series comparisons between in situ spacecraft measurements and magnetohydrodynamic (MHD) model output are constructed via a five-stage pipeline:<br><br>' +
      '1. Ephemeris Retrieval:<br>&emsp;&emsp;Hourly spacecraft positions are obtained from JPL\'s <i>Horizons</i> API within a designated time range.<br>' +
      '2. Instrument Data Acquisition:<br>&emsp;&emsp;In situ instrument measurements are fetched from SPDF\'s <i>COHOWeb</i> database.<br>' +
      '3. Simulation Interpolation:<br>&emsp;&emsp;The spacecraft coordinates are used to interpolate within the selected MHD variable file, yielding simulated values at each time.<br>' +
      '4. Data Cleaning:<br>&emsp;&emsp;Both observed and simulated time series undergo quality-control filters and are converted into consistent physical units.<br>' +
      '5. Comparison:<br>&emsp;&emsp;The paired time series are collated into a single <b><code>astropy</code></b> Table.' +
      '<br>' +
      '<h3>Considerations</h3>' +
      'Run Selection:' +
      '  <blockquote>A valid comparison requires a MAS simulation run whose domain and available variables encompass the requested MHD quantity. Use the <b><code>GET /mas-run-db</code></b> endpoint to identify runs by model type, domain, and temporal coverage. In general, due to the trajectory of most spacecraft missions, heliospheric solutions are preferred; however, during Parker Solar Probe encounters the coronal solution may be employed. If the spacecraft ephemeris extends beyond the MAS run domain, the simulation column will contain nulls for those times.</blockquote>' +
      'Data Availability:' +
      '  <blockquote>Ensure that the selected MAS run’s time span overlaps the spacecraft’s operational interval. Spacecraft availability can be queried via <b><code>GET /spacecraft-db</code></b> endpoint. Note, instrument data availability is seldom continuous; gaps and varying data density should be anticipated when interpreting the resulting time series.</blockquote>' +
      ' Time Range:' +
      '  <blockquote>In the absence of explicit time bounds, the full MAS run start and end times are used. To limit the comparison to a subset of the run, supply one or both of the time range query parameters. Note, “collection” runs – <i>viz.</i> for the Thermo 1, Thermo 2, and Polytropic models – may span multiple decades. It is advisable to constrain such queries with <code>t0</code>/<code>t1</code>' +
      '  to avoid excessive data transfer and potential timeouts.</blockquote>',
    pathParams: [
      {
        name: 'cor_id',
        type: 'integer',
        options: null,
        description: 'Simulation ID.',
      },
      {
        name: 'sc_id',
        type: 'string',
        options: 'omni, psp, solo, stereoa, stereob, ulysses, voyager1, voyager2, pioneer10, pioneer11, pioneerv, helios1, helios2',
        description: 'Spacecraft ID.',
      },
      {
        name: 'domain',
        type: 'string',
        options: 'cor, hel',
        description: 'Simulation domain.',
      },
      {
        name: 'variable',
        type: 'string',
        options: 'br, bt, bp, vr, vt, vp, rho, t',
        description: 'Quantity to compare.',
      },
    ],
    queryParams: [
      {
        name: 't0',
        type: 'datetime',
        options: 'ISO 8601',
        description: 'Start of time range (optional).',
      },
      {
        name: 't1',
        type: 'datetime',
        options: 'ISO 8601',
        description: 'End of time range (optional).',
      },
      {
        name: 'filetype',
        type: 'string',
        options: 'ecsv, csv, dat',
        description: 'Desired output format (default=ecsv).',
      },
    ],
    examples: [
      {
        description: 'Fetch the time series comparison for MAS run 522 – a steady state Thermo 2 run for Carrington Rotation 2284 – and OMNI\'s near-earth data <i>viz.</i> the radial component of velocity.',
        code: [
          {
            form: 'cURL',
            code:
`curl -L '${BASEURL}/api/time-series/522/omni/hel/vr' \\
-H 'Authorization: Api-Key ${APIKEY}' \\
-o time-series_522_omni_hel_vr.ecsv 
`
          },
          {
            form: 'Python',
            code:
`import requests
from io import BytesIO
from astropy.table import Table

url = '${BASEURL}/api/time-series/'

headers = {
    'Authorization': 'Api-Key ${APIKEY}'
}

path_params = {
    'cor_id': '522',
    'sc_id': 'omni',
    'domain': 'hel',
    'variable': 'vr'
}

response = requests.get(url + '/'.join(path_params.values()), headers=headers, stream=True)
response.raise_for_status()
time_series_comparison = Table.read(BytesIO(response.content), format='ascii.ecsv')
`,
          },
          {
            form: 'HTTP',
            code:
`GET /v${VERSION}/api/time-series/522/omni/hel/vr HTTP/1.1
Host: ${BASENAME}
Authorization: Api-Key ${APIKEY}`,
          },
        ]
      },
      {
        description: 'Fetch the time series comparison for MAS run 735 – a PSP run for Parker Encounter 19 – and Parker\'s magnetic field in situ measurements within the coronal domain; the time range has been constrained to the encounter period.',
        code: [
          {
            form: 'cURL',
            code:
`curl -G -L '${BASEURL}/api/time-series/735/psp/cor/br' \\
-H 'Authorization: Api-Key ${APIKEY}' \\
--data-urlencode 't0=2024-03-27T20:00:00' \\
--data-urlencode 't1=2024-04-01T08:00:00' \\
-o time-series_735_p19cor_br.ecsv 
`
          },
          {
            form: 'Python',
            code:
`import requests
from io import BytesIO
from astropy.table import Table

url = '${BASEURL}/api/time-series/'

headers = {
    'Authorization': 'Api-Key ${APIKEY}'
}

path_params = {
    'cor_id': '735',
    'sc_id': 'psp',
    'domain': 'cor',
    'variable': 'br'
}

query_params = {
    't0': '2024-03-27T20:00:00',
    't1': '2024-04-01T08:00:00',
}

response = requests.get(url + '/'.join(path_params.values()), headers=headers, params=query_params, stream=True)
response.raise_for_status()
time_series_comparison = Table.read(BytesIO(response.content), format='ascii.ecsv')
`,
          },
          {
            form: 'HTTP',
            code:
`GET /v${VERSION}/api/time-series/735/psp/cor/br?t0=2024-03-27T20%3A00%3A00&t1=2024-04-01T08%3A00%3A00 HTTP/1.1
Host: ${BASENAME}
Authorization: Api-Key ${APIKEY}`,
          },
        ]
      },
      {
        description: 'Fetch the time series comparison for MAS run 2014 – the global Thermo 2 collection run – and Solar Orbiter\'s plasma density measurements; the time range has been constrained between Carrington Rotations 2287 and 2290, and the output format is set to a fixed-width ASCII file.',
        code: [
          {
            form: 'cURL',
            code:
`curl -G -L '${BASEURL}/api/time-series/2014/solo/hel/rho' \\
-H 'Authorization: Api-Key ${APIKEY}' \\
--data-urlencode 't0=2024-07-27T02:34:16' \\
--data-urlencode 't1=2024-10-16T21:04:00' \\
--data-urlencode 'filetype=dat' \\
-o time-series_2014_solo_rho_cr2297-cr2290.dat 
`
          },
          {
            form: 'Python',
            code:
`import requests
from io import BytesIO
from astropy.table import Table

url = '${BASEURL}/api/time-series/'

headers = {
    'Authorization': 'Api-Key ${APIKEY}'
}

path_params = {
    'cor_id': '2014',
    'sc_id': 'solo',
    'domain': 'hel',
    'variable': 'rho'
}

query_params = {
    't0': '2024-07-27T02:34:16',
    't1': '2024-10-16T21:04:00',
    'filetype': 'dat'
}

response = requests.get(url + '/'.join(path_params.values()), headers=headers, params=query_params, stream=True)
response.raise_for_status()
with open('time-series_2014_solo_rho_cr2297-cr2290.dat', 'wb') as f:
    for chunk in response.iter_content(chunk_size=8192):
        f.write(chunk)
`,
          },
          {
            form: 'HTTP',
            code:
`GET /v${VERSION}/api/time-series/2014/solo/hel/rho?t0=2024-07-27T02%3A34%3A16&t1=2024-10-16T21%3A04%3A00&filetype=dat HTTP/1.1
Host: ${BASENAME}
Authorization: Api-Key ${APIKEY}`,
          },
        ]
      },
    ],
  },
  {
    title: 'Spacecraft Mapping',
    method: 'GET',
    path: '/spacecraft-mapping/{cor_id}/{sc_id}',
    description: 'Retrieve the spacecraft mapping for a given simulation and spacecraft.',
    notes: 'Spacecraft mappings are calculated using a twofold approach:<br><br>' +
      '1. Ballistic Mapping:<blockquote>Hourly spacecraft positions are obtained from JPL\'s <i>Horizons</i> API within a designated time range. In situ instrument measurements are fetched from SPDF\'s <i>COHOWeb</i> database for proton flow speed. For positions with missing or corrupted instrument measurements, a default flow speed of 400 km/s is used. Spacecraft ephemeris, proton flow speed, and the Sun\'s sidereal rotation are used to ballistically map particles to the given MAS run\'s coronal boundary (generally 30 R⊙).</blockquote>' +
      '2. Fieldline Tracing:<blockquote>The ballistically mapped positions are then used as launch points to trace fieldlines through the MAS run\'s coronal magnetic field, back to the Sun\'s photosphere <i>i.e.</i> 1 R⊙. For instances where a spacecraft\'s trajectory lies within the coronal boundary – <i>e.g.</i> Parker Encounters – no ballistic mapping is performed; instead, the spacecraft\'s positions are used as the fieldline tracing launch points.</blockquote>' +
      '<br>' +
      '<h3>Considerations</h3>' +
      'Run Selection:' +
      '  <blockquote>A valid spacecraft mapping requires a MAS run that possesses a solution for the coronal domain, and the vector components for the magnetic field. Use the <b><code>GET /mas-run-db</code></b> endpoint to identify runs by model type, domain, and temporal coverage. </blockquote>' +
      'Data Availability:' +
      '  <blockquote>Ensure that the selected MAS run’s time span overlaps the spacecraft’s operational interval. Spacecraft availability can be queried via <b><code>GET /spacecraft-db</code></b> endpoint. <b>Note, ballistic mapping results in a (backward) temporal shift; therefore, spacecraft ephemeris near a MAS run\'s start time often map back <i>outside</i> the run\'s time range.</b></blockquote>' +
      ' Time Range:' +
      '  <blockquote>In the absence of explicit time bounds, the full MAS run start and end times are used. To limit the comparison to a subset of the run, supply one or both of the time range query parameters. Note, “collection” runs – <i>viz.</i> for the Thermo 1, Thermo 2, and Polytropic models – may span multiple decades. It is advisable to constrain such queries with <code>t0</code>/<code>t1</code>to avoid excessive data transfer and potential timeouts.</blockquote>' +
      'Multi-State Runs:' +
      '  <blockquote>For MAS runs that contain more than one state, the timestamp for each ballistically mapped position is mapped to the appropriate state (using the state\'s time range); fieldline tracing is then performed using the corresponding state\'s magnetic field data files.</blockquote>',
    pathParams: [
      {
        name: 'cor_id',
        type: 'integer',
        options: null,
        description: 'Simulation ID.',
      },
      {
        name: 'sc_id',
        type: 'string',
        options: 'omni, psp, solo, stereoa, stereob, ulysses, voyager1, voyager2, pioneer10, pioneer11, pioneerv, helios1, helios2, soho, bepi, maven, messenger, mercury, venus, earth',
        description: 'Spacecraft ID.',
      },
    ],
    queryParams: [
      {
        name: 't0',
        type: 'datetime',
        options: 'ISO 8601',
        description: 'Start of time range (optional).',
      },
      {
        name: 't1',
        type: 'datetime',
        options: 'ISO 8601',
        description: 'End of time range (optional).',
      },
      {
        name: 'filetype',
        type: 'string',
        options: 'ecsv, csv, json',
        description: 'Desired output format (default=ecsv).',
      },
    ],
    examples: [
      {
        description: 'Fetch the spacecraft mapping for MAS run 522 – a steady state Thermo 2 run for Carrington Rotation 2284 – and OMNI.',
        code: [
          {
            form: 'cURL',
            code:
`curl -L '${BASEURL}/api/spacecraft-mapping/522/omni' \\
-H 'Authorization: Api-Key ${APIKEY}' \\
-o spacecraft-mapping_522_omni.ecsv 
`
          },
          {
            form: 'Python',
            code:
`import requests
from io import BytesIO
from astropy.table import Table

url = '${BASEURL}/api/spacecraft-mapping/'

headers = {
    'Authorization': 'Api-Key ${APIKEY}'
}

path_params = {
    'cor_id': '522',
    'sc_id': 'omni',
}

response = requests.get(url + '/'.join(path_params.values()), headers=headers, stream=True)
response.raise_for_status()
spacecraft_mapping = Table.read(BytesIO(response.content), format='ascii.ecsv')
`,
          },
          {
            form: 'HTTP',
            code:
`GET /v${VERSION}/api/spacecraft-mapping/522/omni/hel/vr HTTP/1.1
Host: ${BASENAME}
Authorization: Api-Key ${APIKEY}`,
          },
        ]
      },
      {
        description: 'Fetch the spacecraft mapping for MAS run 735 – a PSP run for Parker Encounter 19 – and Parker; the time range has been constrained to the encounter period (±1 day).',
        code: [
          {
            form: 'cURL',
            code:
`curl -G -L '${BASEURL}/api/spacecraft-mapping/735/psp' \\
-H 'Authorization: Api-Key ${APIKEY}' \\
--data-urlencode 't0=2024-03-26T20:00:00' \\
--data-urlencode 't1=2024-04-02T08:00:00' \\
-o spacecraft-mapping_735_p19cor.ecsv 
`
          },
          {
            form: 'Python',
            code:
`import requests
from io import BytesIO
from astropy.table import Table

url = '${BASEURL}/api/spacecraft-mapping/'

headers = {
    'Authorization': 'Api-Key ${APIKEY}'
}

path_params = {
    'cor_id': '735',
    'sc_id': 'psp',
}

query_params = {
    't0': '2024-03-26T20:00:00',
    't1': '2024-04-02T08:00:00',
}

response = requests.get(url + '/'.join(path_params.values()), headers=headers, params=query_params, stream=True)
response.raise_for_status()
spacecraft_mapping = Table.read(BytesIO(response.content), format='ascii.ecsv')
`,
          },
          {
            form: 'HTTP',
            code:
`GET /v${VERSION}/api/spacecraft-mapping/735/psp?t0=2024-03-26T20%3A00%3A00&t1=2024-04-02T08%3A00%3A00 HTTP/1.1
Host: ${BASENAME}
Authorization: Api-Key ${APIKEY}`,
          },
        ]
      },
      {
        description: 'Fetch the spacecraft mapping for MAS run 2014 – the global Thermo 2 collection run – and Solar Orbiter; the time range has been constrained between Carrington Rotations 2287 and 2290, and the output format is set to a fixed-width ASCII file.',
        code: [
          {
            form: 'cURL',
            code:
`curl -G -L '${BASEURL}/api/spacecraft-mapping/2014/solo' \\
-H 'Authorization: Api-Key ${APIKEY}' \\
--data-urlencode 't0=2024-07-27T02:34:16' \\
--data-urlencode 't1=2024-10-16T21:04:00' \\
--data-urlencode 'filetype=dat' \\
-o spacecraft-mapping_2014_solo_cr2297-cr2290.dat 
`
          },
          {
            form: 'Python',
            code:
`import requests
from io import BytesIO
from astropy.table import Table

url = '${BASEURL}/api/spacecraft-mapping/'

headers = {
    'Authorization': 'Api-Key ${APIKEY}'
}

path_params = {
    'cor_id': '2014',
    'sc_id': 'solo',
}

query_params = {
    't0': '2024-07-27T02:34:16',
    't1': '2024-10-16T21:04:00',
    'filetype': 'dat'
}

response = requests.get(url + '/'.join(path_params.values()), headers=headers, params=query_params, stream=True)
response.raise_for_status()
with open('spacecraft-mapping_2014_solo_cr2297-cr2290.dat', 'wb') as f:
    for chunk in response.iter_content(chunk_size=8192):
        f.write(chunk)
`,
          },
          {
            form: 'HTTP',
            code:
`GET /v${VERSION}/api/spacecraft-mapping/2014/solo?t0=2024-07-27T02%3A34%3A16&t1=2024-10-16T21%3A04%3A00&filetype=dat HTTP/1.1
Host: ${BASENAME}
Authorization: Api-Key ${APIKEY}`,
          },
        ]
      },
    ],
  },
]


export const examples = [
  {
    title: 'Example #1',
    code: `"""
Slicing Time Dependent Runs
===========================
This example illustrates how to perform a multi-parameter query the MAS Run Database,
fetch metadata about a specific (viz. time dependent) run, and extract 2D slices
at a specific radius over a time range that encompasses a time of interest.
"""
import os
import requests
import numpy as np

# For security, a user's secret API key should be stored as an environment variable.
# Here, the key is saved in the USER_TOKEN variable, however this can be tailored to
# suit user preferences.
BASE_URL = 'https://www.predsci.com/mhdweb2_bu/v2/api'
USER_TOKEN = os.environ.get('USER_TOKEN')
HEADERS = {
    'Accept': 'application/json',
    'Authorization': f"Api-Key {USER_TOKEN}"
}

# The MAS Run Database is queried for simulations that meet specific criteria, viz.
# all time dependent 'Eclipse' runs – with a coronal domain, and data files for the
# radial component of the magnetic field – whose run time range encompasses a specified
# time of interest (the 2024 Total Solar Eclipse).
DOMAIN = 'cor'
TIME_OF_INTEREST = '2024-04-08T09:00:00'
PARAMS = {
    'toi': TIME_OF_INTEREST,
    'model': 'eclipse',
    'type': 'td',
    'domain': DOMAIN,
    'variables': 'br'
}

print("Querying MAS Run DB ...")
db_response = requests.get(f'{BASE_URL}/mas-run-db',
                           headers=HEADERS,
                           params=PARAMS)
db_response.raise_for_status()

# The result of this query is a list of runs that meet the above criteria; each entry in
# this list contains metadata for the corresponding run, including id, name, and time range.
try:
    run = db_response.json()[0]
except IndexError:
    raise IndexError("No results found in MAS Run DB for the specified parameters.")

# The "id" field (or "cor_id") is a unique identifier assigned to each simulation within the 
# database. This identifier is used throughout the MHDweb API to indicate which simulation the
# specific data product is being applied to. Here, we use the run's id to query for the list 
# of states associated with the Eclipse 2024 time dependent run.
print(f"Querying MAS Run '{run['id']}' ...")
eclipse_response = requests.get(f"{BASE_URL}/mas-run-db/{run['id']}",
                                headers=HEADERS)
eclipse_response.raise_for_status()
try:
    states = eclipse_response.json()[DOMAIN]['states']
except KeyError:
    raise KeyError(f"No '{DOMAIN}' domain states found for the specified MAS run.")

# The 'states' list for the Eclipse 2024 run contains over 750 entries – hourly time steps from
# 2024-03-16 to 2024-04-17. In this example, we examine a subset of these states, viz. those
# that fall within 24 hours before and after the time of interest.
toi = np.datetime64(TIME_OF_INTEREST)
toi_range_t0 = toi - np.timedelta64(1, 'D')
toi_range_t1 = toi + np.timedelta64(1, 'D')

# Determine the indices of the states that correspond to the time range of interest.

index_t0 = next((i for i, s in enumerate(states)
                 if np.datetime64(s['state_t0']) < toi_range_t0 < np.datetime64(s['state_t1'])),
                0)
index_t1 = next((i for i, s in enumerate(states)
                 if np.datetime64(s['state_t0']) < toi_range_t1 < np.datetime64(s['state_t1'])),
                len(states)-1)

# With the time range indices determined, we can now loop over the states in this range
# and extract 2D slices at a specified radius (here, r=1.0 Rsun) for the radial magnetic
# field ('br') variable. The slices are stored in a list for further analysis or visualization.
slice_params = dict(r=1.0)
slice_list = []

slice_response = requests.get(f"{BASE_URL}/slice-3d/{run['id']}/{DOMAIN}/{index_t0}/br",
                              headers=HEADERS,
                              params=slice_params)
slice_response.raise_for_status()

# Each response contains the slice's scales and metadata; given that these are consistent
# across all states (when using a fixed slice value), we extract them once here for reference.
slice_meta = {key: value for key, value in slice_response.json().items() if key != 'values'}
for state_i in range(index_t0, index_t1 + 1):
    print(f"Fetching State: {states[state_i]} ...")
    response = requests.get(f"{BASE_URL}/slice-3d/{run['id']}/{DOMAIN}/{state_i}/br",
                            headers=HEADERS,
                            params=slice_params)
    slice_list.append(np.asarray(response.json()['values']))

# Note, the 'values' field in each response contains the 2D slice data as a nested list,
# which is converted to a NumPy array for easier manipulation and vectorized computations.
`
  },
  {
    title: 'Example #2',
    code: `"""
Multi Run/Spacecraft Time-Series
================================
This example demonstrates how to generate time series comparisons across multiple
simulation runs and spacecraft; specifically, it retrieves time series data for
Thermo 1, Thermo 2, and Polytropic "collection" runs over a six-month period in 2024. 
"""
import os
import requests
from io import BytesIO
from astropy.table import Table

# For security, a user's secret API key should be stored as an environment variable.
# Here, the key is saved in the USER_TOKEN variable, however this can be tailored to
# suit user preferences.
BASE_URL = 'https://www.predsci.com/mhdweb2_bu/v2/api'
USER_TOKEN = os.environ.get('USER_TOKEN')
HEADERS = {
    'Accept': 'application/json',
    'Authorization': f"Api-Key {USER_TOKEN}"
}

# The MAS Run Database is queried for simulations that meet specific criteria, viz.
# all Carrington collection ('co') runs that include data files for Br, Vr, and T.
SPACECRAFTS_OF_INTEREST = ('omni', 'psp', 'solo')
VARIABLES_OF_INTEREST = ('br', 'vr', 't')
PARAMS = {
    'model': 'carrington',
    'type': 'co',
    'variables': list(VARIABLES_OF_INTEREST),
}

print("Querying MAS Run DB ...")
db_response = requests.get(f'{BASE_URL}/mas-run-db', headers=HEADERS, params=PARAMS)
db_response.raise_for_status()
try:
    runs = db_response.json()
except IndexError:
    raise ValueError("No results found in MAS Run DB for the specified parameters.")

# The result of this database query is a list of 3 runs, each corresponding to a different
# model type (Thermo 1, Thermo 2, Polytropic). For each run, we loop over the
# spacecraft and variables of interest, querying the Time-Series Comparison endpoint
# to retrieve the relevant time series data over a six-month period in 2024.
time_series = {}
time_range = dict(t0='2024-01-01T00:00:00', t1='2024-07-01T00:00:00')

for run in runs:
    time_series[run['name']] = {}
    for spacecraft in SPACECRAFTS_OF_INTEREST:
        time_series[run['name']][spacecraft] = None
        for variable in VARIABLES_OF_INTEREST:
            print(f"Querying Time-Series Comparison for Run:"
                  f" '{run['id']}', "
                  f"Variable '{variable}', "
                  f"Spacecraft '{spacecraft}' ...")
            ts_response = requests.get(
                f"{BASE_URL}/time-series/{run['id']}/{spacecraft}/hel/{variable}",
                headers=HEADERS,
                params=time_range
            )
            ts_response.raise_for_status()
# The time series data is returned in ECSV format, which is read into an Astropy Table.
# If this is the first variable being processed for the given spacecraft/run, the entire
# table is stored. For subsequent variables, only the relevant columns are added
# to the existing table – either "spacecraft_" or "simulation_" followed by the variable.  
            table = Table.read(BytesIO(ts_response.content), format='ascii.ecsv')
            if not time_series[run['name']][spacecraft]:
                time_series[run['name']][spacecraft] = table
            else:
                time_series[run['name']][spacecraft][f'spacecraft_{variable}'] = table[f'spacecraft_{variable}']
                time_series[run['name']][spacecraft][f'simulation_{variable}'] = table[f'simulation_{variable}']
# The resulting dictionary of astropy Tables contains ephemeris for each spacecraft, along
# with time series for Br, Vr, and T from both the spacecraft observations and the
# corresponding simulation runs. This data can then be used for further analysis
# and visualization as needed.              
`
  },
  {
    title: 'Example #3',
    code: `"""
Fetching HDF5 Data for Multiple MAS Runs
========================================
This script queries the MAS Run Database for steady-state runs that use HMI surface data
and retrieves HDF5 data files for the coronal and heliospheric domains over a specified
time range centered around the 2024 Total Solar Eclipse.
"""
import os
import requests
import numpy as np

# For security, a user's secret API key should be stored as an environment variable.
# Here, the key is saved in the USER_TOKEN variable, however this can be tailored to
# suit user preferences.
BASE_URL = 'https://www.predsci.com/mhdweb2_bu/v2/api'
USER_TOKEN = os.environ.get('USER_TOKEN')
HEADERS = {
    'Accept': 'application/json',
    'Authorization': f"Api-Key {USER_TOKEN}"
}

# The MAS Run Database is queried for steady-state ('ss') Thermo 2 simulations that have run
# times encompassing a 180-day window centered on the 2024 Total Solar Eclipse.
TIME_OF_INTEREST = '2024-04-08T09:00:00'
TIME_OF_INTEREST = np.datetime64(TIME_OF_INTEREST)
TIME_RANGE_T0 = TIME_OF_INTEREST - np.timedelta64(90, 'D')
TIME_RANGE_T1 = TIME_OF_INTEREST + np.timedelta64(90, 'D')

PARAMS = {
    't0': str(TIME_RANGE_T0),
    't1': str(TIME_RANGE_T1),
    'model': 'thermo_2',
    'type': 'ss',
}

print("Querying MAS Run DB ...")
db_response = requests.get(f'{BASE_URL}/mas-run-db', headers=HEADERS, params=PARAMS)
db_response.raise_for_status()
try:
    runs = db_response.json()
except IndexError:
    raise ValueError("No results found in MAS Run DB for the specified parameters.")

# The result of this database query is a list of runs that meet the above criteria.
# These runs are sorted by their start time, and filtered for those that use HMI
# surface data.
runs.sort(key=lambda x: np.datetime64(x['start_time']))
hmi_runs = filter(lambda x: x['surface_data_source'] == 'hmi', runs)

# For each of the resulting runs, we loop over the coronal ('cor') and heliospheric ('hel')
# domains, querying the MAS Run Database to download the corresponding HDF5 data files
# for the variables Br, Vr, and T.
for run in hmi_runs:
    for domain in ('cor', 'hel'):
        path_params = {
            'cor_id': str(run['id']),
            'domain': domain,
            'state': '0',   # steady-state runs have a single state (a list with 1 element at index 0)
            'variable': 'br,vr,t'
        }
        response = requests.get(f"{BASE_URL}/mas-run-db/" + '/'.join(path_params.values()),
                                headers={'Authorization': f"Api-Key {USER_TOKEN}"},
                                stream=True)
        response.raise_for_status()
        with open(f"mas_run_{run['id']}_{domain}_br-vr-t.zip", "wb") as f:
            for chunk in response.iter_content(chunk_size=8192):
                f.write(chunk)

# The resulting zip files contain the HDF5 data files for Br, Vr, and T for each domain.
# Although the files are saved in the current working directory in this example, users may
# choose to organize them in a different manner as needed.
`
  }
]